/**
 * Study Filters Component
 * 
 * Left sidebar with filtering options for the Study Library.
 * Includes symbol, timeframe, and freshness filters plus search.
 */

import { useCallback } from "react";
import {
  Clock,
  Filter,
  Layers,
  ListFilter,
  Search,
  Tag,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/utils/cn";
import {
  type FreshnessStatus,
  type LabelSummary,
  type ParallelFilterMode,
  getFreshnessColorClass,
  getFreshnessLabel,
  getTimeframeDisplayName,
} from "@/utils/studyGrouping";
import { getLabelColorStyle } from "@/utils/labelColors";

// =============================================================================
// Types
// =============================================================================

interface StudyFiltersProps {
  allSymbols: string[];
  allTimeframes: string[];
  selectedSymbols: string[];
  selectedTimeframes: string[];
  selectedFreshness: FreshnessStatus[];
  selectedParallelMode: ParallelFilterMode;
  searchQuery: string;
  showLabeledOnly: boolean;
  allLabels: LabelSummary[];
  selectedLabels: string[];
  onSymbolChange: (symbols: string[]) => void;
  onTimeframeChange: (timeframes: string[]) => void;
  onFreshnessChange: (freshness: FreshnessStatus[]) => void;
  onParallelModeChange: (mode: ParallelFilterMode) => void;
  onSearchChange: (query: string) => void;
  onShowLabeledOnlyChange: (value: boolean) => void;
  onLabelToggle: (label: string) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
}

// =============================================================================
// Main Component
// =============================================================================

export function StudyFilters({
  allSymbols,
  allTimeframes,
  selectedSymbols,
  selectedTimeframes,
  selectedFreshness,
  selectedParallelMode,
  searchQuery,
  showLabeledOnly,
  allLabels,
  selectedLabels,
  onSymbolChange,
  onTimeframeChange,
  onFreshnessChange,
  onParallelModeChange,
  onSearchChange,
  onShowLabeledOnlyChange,
  onLabelToggle,
  onClearAll,
  hasActiveFilters,
}: StudyFiltersProps) {
  // Toggle handlers
  const toggleSymbol = useCallback(
    (symbol: string) => {
      if (selectedSymbols.includes(symbol)) {
        onSymbolChange(selectedSymbols.filter((s) => s !== symbol));
      } else {
        onSymbolChange([...selectedSymbols, symbol]);
      }
    },
    [selectedSymbols, onSymbolChange]
  );

  const toggleTimeframe = useCallback(
    (timeframe: string) => {
      if (selectedTimeframes.includes(timeframe)) {
        onTimeframeChange(selectedTimeframes.filter((t) => t !== timeframe));
      } else {
        onTimeframeChange([...selectedTimeframes, timeframe]);
      }
    },
    [selectedTimeframes, onTimeframeChange]
  );

  const toggleFreshness = useCallback(
    (freshness: FreshnessStatus) => {
      if (selectedFreshness.includes(freshness)) {
        onFreshnessChange(selectedFreshness.filter((f) => f !== freshness));
      } else {
        onFreshnessChange([...selectedFreshness, freshness]);
      }
    },
    [selectedFreshness, onFreshnessChange]
  );

  const studyTypeLabel =
    selectedParallelMode === "parallel"
      ? "Parallel"
      : selectedParallelMode === "standalone"
        ? "Standalone"
        : null;

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-dark-500" />
        <Input
          placeholder="Search studies..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 bg-dark-900/50 border-dark-700 focus:border-blue-500"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-500 hover:text-dark-300"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Clear filters button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="w-full text-dark-400 hover:text-dark-200 justify-start gap-2"
        >
          <X className="h-4 w-4" />
          Clear all filters
        </Button>
      )}

      {/* Filter sections */}
      <div className="bg-dark-900/30 rounded-lg border border-dark-800">
        <Accordion
          type="multiple"
          defaultValue={["study-type", "labels", "symbols", "timeframes", "freshness"]}
          className="w-full"
        >
          {/* Study type filter */}
          <AccordionItem value="study-type" className="border-b border-dark-800">
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-dark-800/50">
              <div className="flex items-center gap-2 text-sm font-medium text-dark-200">
                <ListFilter className="h-4 w-4 text-emerald-400" />
                Study Type
                {studyTypeLabel && (
                  <span className="ml-auto mr-2 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                    {studyTypeLabel}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-3">
              <div className="space-y-1">
                <StudyTypeOption
                  label="All Studies"
                  description="Parallel and standalone"
                  checked={selectedParallelMode === "all"}
                  onChange={() => onParallelModeChange("all")}
                />
                <StudyTypeOption
                  label="Parallel Only"
                  description="Grouped parallel runs"
                  checked={selectedParallelMode === "parallel"}
                  onChange={() => onParallelModeChange("parallel")}
                />
                <StudyTypeOption
                  label="Standalone Only"
                  description="One-off studies"
                  checked={selectedParallelMode === "standalone"}
                  onChange={() => onParallelModeChange("standalone")}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Labels filter (AIE-539) — orthogonal to Study Type; layers on top of it */}
          <AccordionItem value="labels" className="border-b border-dark-800">
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-dark-800/50">
              <div className="flex items-center gap-2 text-sm font-medium text-dark-200">
                <Tag className="h-4 w-4 text-amber-400" />
                Labels
                {(selectedLabels.length > 0 || showLabeledOnly) && (
                  <span className="ml-auto mr-2 text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
                    {selectedLabels.length > 0 ? selectedLabels.length : "On"}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-3">
              <div className="space-y-1">
                <FilterCheckbox
                  label="Show labeled only"
                  checked={showLabeledOnly}
                  onChange={() => onShowLabeledOnlyChange(!showLabeledOnly)}
                />

                {allLabels.length > 0 && (
                  <>
                    <p className="px-2 pt-2 pb-1 text-[11px] uppercase tracking-wide text-dark-500">
                      Filter by label
                    </p>
                    {allLabels.map((l) => (
                      <LabelFilterRow
                        key={l.label}
                        label={l.label}
                        color={l.color}
                        count={l.count}
                        checked={selectedLabels.includes(l.label)}
                        onChange={() => onLabelToggle(l.label)}
                      />
                    ))}
                  </>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Symbols filter */}
          <AccordionItem value="symbols" className="border-b border-dark-800">
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-dark-800/50">
              <div className="flex items-center gap-2 text-sm font-medium text-dark-200">
                <Layers className="h-4 w-4 text-blue-400" />
                Symbols
                {selectedSymbols.length > 0 && (
                  <span className="ml-auto mr-2 text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                    {selectedSymbols.length}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-3">
              <div className="space-y-1">
                {allSymbols.length === 0 ? (
                  <p className="text-xs text-dark-500">No symbols found</p>
                ) : (
                  allSymbols.map((symbol) => (
                    <FilterCheckbox
                      key={symbol}
                      label={symbol}
                      checked={selectedSymbols.includes(symbol)}
                      onChange={() => toggleSymbol(symbol)}
                    />
                  ))
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Timeframes filter */}
          <AccordionItem value="timeframes" className="border-b border-dark-800">
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-dark-800/50">
              <div className="flex items-center gap-2 text-sm font-medium text-dark-200">
                <Clock className="h-4 w-4 text-cyan-400" />
                Timeframes
                {selectedTimeframes.length > 0 && (
                  <span className="ml-auto mr-2 text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">
                    {selectedTimeframes.length}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-3">
              <div className="space-y-1">
                {allTimeframes.length === 0 ? (
                  <p className="text-xs text-dark-500">No timeframes found</p>
                ) : (
                  allTimeframes.map((timeframe) => (
                    <FilterCheckbox
                      key={timeframe}
                      label={getTimeframeDisplayName(timeframe)}
                      checked={selectedTimeframes.includes(timeframe)}
                      onChange={() => toggleTimeframe(timeframe)}
                    />
                  ))
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Freshness filter */}
          <AccordionItem value="freshness" className="border-none">
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-dark-800/50">
              <div className="flex items-center gap-2 text-sm font-medium text-dark-200">
                <Filter className="h-4 w-4 text-purple-400" />
                Freshness
                {selectedFreshness.length > 0 && (
                  <span className="ml-auto mr-2 text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                    {selectedFreshness.length}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-3">
              <div className="space-y-1">
                <FreshnessCheckbox
                  freshness="fresh"
                  checked={selectedFreshness.includes("fresh")}
                  onChange={() => toggleFreshness("fresh")}
                />
                <FreshnessCheckbox
                  freshness="aging"
                  checked={selectedFreshness.includes("aging")}
                  onChange={() => toggleFreshness("aging")}
                />
                <FreshnessCheckbox
                  freshness="stale"
                  checked={selectedFreshness.includes("stale")}
                  onChange={() => toggleFreshness("stale")}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Help text */}
      <p className="text-xs text-dark-500 px-1">
        Studies older than 30 days are marked stale and may need re-running with fresh data.
      </p>
    </div>
  );
}

// =============================================================================
// Filter Checkbox Components
// =============================================================================

interface FilterCheckboxProps {
  label: string;
  checked: boolean;
  onChange: () => void;
}

function FilterCheckbox({ label, checked, onChange }: FilterCheckboxProps) {
  return (
    <button
      onClick={onChange}
      className={cn(
        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors",
        checked
          ? "bg-blue-500/10 text-blue-400"
          : "text-dark-400 hover:bg-dark-800 hover:text-dark-200"
      )}
    >
      <div
        className={cn(
          "w-4 h-4 rounded border flex items-center justify-center transition-colors",
          checked
            ? "bg-blue-500 border-blue-500"
            : "border-dark-600 bg-transparent"
        )}
      >
        {checked && (
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>
      <span className="truncate">{label}</span>
    </button>
  );
}

// AIE-539: a checkbox row for filtering by a specific label — a colored dot in the
// label's category color, the label text (truncated), and its study count.
interface LabelFilterRowProps {
  label: string;
  color: string;
  count: number;
  checked: boolean;
  onChange: () => void;
}

function LabelFilterRow({ label, color, count, checked, onChange }: LabelFilterRowProps) {
  return (
    <button
      onClick={onChange}
      title={label}
      className={cn(
        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors",
        checked
          ? "bg-amber-500/10 text-amber-300"
          : "text-dark-400 hover:bg-dark-800 hover:text-dark-200"
      )}
    >
      <div
        className={cn(
          "w-4 h-4 rounded border flex items-center justify-center transition-colors shrink-0",
          checked ? "bg-amber-500 border-amber-500" : "border-dark-600 bg-transparent"
        )}
      >
        {checked && (
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <span className={cn("h-2.5 w-2.5 rounded-full shrink-0", getLabelColorStyle(color).swatch)} />
      <span className="truncate flex-1 text-left">{label}</span>
      <span className="text-xs text-dark-500 shrink-0">{count}</span>
    </button>
  );
}

interface FreshnessCheckboxProps {
  freshness: FreshnessStatus;
  checked: boolean;
  onChange: () => void;
}

interface StudyTypeOptionProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}

function StudyTypeOption({ label, description, checked, onChange }: StudyTypeOptionProps) {
  return (
    <button
      onClick={onChange}
      className={cn(
        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors",
        checked
          ? "bg-emerald-500/10 text-emerald-400"
          : "text-dark-400 hover:bg-dark-800 hover:text-dark-200"
      )}
    >
      <div
        className={cn(
          "w-4 h-4 rounded-full border flex items-center justify-center transition-colors",
          checked
            ? "bg-emerald-500 border-emerald-500"
            : "border-dark-600 bg-transparent"
        )}
      >
        {checked && <div className="w-2 h-2 rounded-full bg-white" />}
      </div>
      <div className="flex flex-col items-start leading-tight">
        <span>{label}</span>
        <span className="text-xs text-dark-500">{description}</span>
      </div>
    </button>
  );
}

function FreshnessCheckbox({ freshness, checked, onChange }: FreshnessCheckboxProps) {
  const colorClass = getFreshnessColorClass(freshness);
  const label = getFreshnessLabel(freshness);

  const descriptions: Record<FreshnessStatus, string> = {
    fresh: "< 14 days",
    aging: "14-30 days",
    stale: "> 30 days",
  };

  return (
    <button
      onClick={onChange}
      className={cn(
        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors",
        checked
          ? "bg-dark-800"
          : "text-dark-400 hover:bg-dark-800 hover:text-dark-200"
      )}
    >
      <div
        className={cn(
          "w-4 h-4 rounded border flex items-center justify-center transition-colors",
          checked
            ? "bg-blue-500 border-blue-500"
            : "border-dark-600 bg-transparent"
        )}
      >
        {checked && (
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>
      <span className={cn("px-1.5 py-0.5 rounded text-xs border", colorClass)}>
        {label}
      </span>
      <span className="text-dark-500 text-xs">{descriptions[freshness]}</span>
    </button>
  );
}

export default StudyFilters;
